import { CSSProperties, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import css from './segmented.module.css'
import cn from '@/utils/cn.ts'

type SegmentedValue = string | number

export type SegmentedSize = 'small' | 'middle' | 'large'

export type SegmentedOption =
  | SegmentedValue
  | {
      label?: ReactNode
      value: SegmentedValue
      icon?: ReactNode
      disabled?: boolean
      className?: string
    }

type NormalizedOption = {
  label: ReactNode
  value: SegmentedValue
  icon?: ReactNode
  disabled?: boolean
  className?: string
}

interface IProps {
  options: SegmentedOption[]
  value?: SegmentedValue
  defaultValue?: SegmentedValue
  onChange?: (value: SegmentedValue) => void
  size?: SegmentedSize
  block?: boolean
  disabled?: boolean
  className?: string
  style?: CSSProperties
}

const toKey = (value: SegmentedValue) => value.toString()

const Segmented = ({ size = 'middle', block, disabled, ...props }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const normalizedOptions = useMemo<NormalizedOption[]>(() => {
    return props.options.map((option) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return { label: option, value: option }
      }

      return {
        label: option.label ?? option.value,
        value: option.value,
        icon: option.icon,
        disabled: option.disabled,
        className: option.className,
      }
    })
  }, [props.options])

  const firstValue = normalizedOptions[0]?.value

  const [innerValue, setInnerValue] = useState<SegmentedValue | undefined>(() => {
    if (props.value !== undefined) return props.value
    if (props.defaultValue !== undefined) return props.defaultValue
    return firstValue
  })

  useEffect(() => {
    if (props.value !== undefined) {
      setInnerValue(props.value)
    }
  }, [props.value])

  useEffect(() => {
    if (props.value === undefined && firstValue !== undefined && !normalizedOptions.find((o) => o.value === innerValue)) {
      setInnerValue(firstValue)
    }
  }, [firstValue, innerValue, normalizedOptions, props.value])

  const currentValue = props.value !== undefined ? props.value : innerValue

  const updateThumbPosition = useCallback(() => {
    if (!containerRef.current || !thumbRef.current || currentValue === undefined) return

    const activeEl = optionRefs.current[toKey(currentValue)]
    if (!activeEl) {
      thumbRef.current.style.opacity = '0'
      return
    }

    thumbRef.current.style.width = `${activeEl.offsetWidth}px`
    thumbRef.current.style.transform = `translateX(${activeEl.offsetLeft}px)`
    thumbRef.current.style.opacity = '1'
  }, [currentValue])

  useLayoutEffect(() => {
    updateThumbPosition()
  }, [updateThumbPosition, normalizedOptions.length, size, block])

  useEffect(() => {
    const handleResize = () => updateThumbPosition()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updateThumbPosition])

  const handleSelect = (value: SegmentedValue, optionDisabled?: boolean) => {
    if (disabled || optionDisabled) return
    if (currentValue === value) return

    if (props.value === undefined) {
      setInnerValue(value)
    }
    props.onChange?.(value)
  }

  return (
    <div
      ref={containerRef}
      className={cn(css.container, props.className)}
      data-size={size}
      data-block={block}
      data-disabled={disabled}
      style={props.style}
    >
      <div ref={thumbRef} className={css.slider} aria-hidden />
      {normalizedOptions.map((option) => (
        <button
          key={toKey(option.value)}
          ref={(el) => (optionRefs.current[toKey(option.value)] = el)}
          type="button"
          className={cn(css.item, 'text-nm', option.className)}
          data-active={currentValue === option.value}
          data-size={size}
          disabled={disabled || option.disabled}
          onClick={() => handleSelect(option.value, option.disabled)}
        >
          {option.icon && <span className={css.icon}>{option.icon}</span>}
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default Segmented
